import React from 'react';
import { motion } from 'framer-motion';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import { ArrowRight, TrendingUp, Flame, Eye, Calendar } from 'lucide-react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

// Import Swiper modules
import 'swiper/css/bundle';

// Import styles
import styles from './WhatsHot.module.css';

const WhatsHot: React.FC = () => {
  const trendingTopics = [
    {
      id: 1,
      title: "VR in Competitive Gaming",
      description: "Explore how virtual reality is revolutionizing esports with immersive competitive experiences and new tournament formats.",
      image: "https://images.unsplash.com/photo-1592478411213-6153e4ebc696?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80",
      category: "VR Gaming",
      views: "2.3K",
      date: "2 days ago",
      trending: true
    },
    {
      id: 2,
      title: "Top Esports Gear of the Year",
      description: "Professional gamers reveal their secret weapons - the peripherals and hardware that give them the competitive edge.",
      image: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80",
      category: "Esports",
      views: "5.7K",
      date: "1 day ago",
      trending: true
    },
    {
      id: 3,
      title: "Cloud Gaming: Is It Finally Here?",
      description: "Analyzing the current state of cloud gaming platforms and whether they're ready to replace traditional gaming setups.",
      image: "https://images.unsplash.com/photo-1560472355-536de3962603?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80",
      category: "Cloud Gaming",
      views: "4.1K",
      date: "3 days ago",
      trending: false
    },
    {
      id: 4,
      title: "AI-Enhanced Game Graphics",
      description: "How machine learning is creating photorealistic visuals and revolutionizing game development workflows.",
      image: "https://images.unsplash.com/photo-1551103782-8ab07afd45c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80",
      category: "AI Technology",
      views: "3.9K",
      date: "4 days ago",
      trending: false
    },
    {
      id: 5,
      title: "Next-Gen Console Exclusives",
      description: "Breaking down the most anticipated exclusive titles coming to PlayStation 5 and Xbox Series X in 2025.",
      image: "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80",
      category: "Console Gaming",
      views: "6.2K",
      date: "5 days ago",
      trending: true
    }
  ];

  return (
    <section id="whats-hot" className="py-20 bg-gradient-to-b from-dark-800 to-dark-900 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center mb-6">
            <Flame className="w-8 h-8 text-red-500 mr-3 animate-pulse" />
            <h2 className="text-4xl md:text-5xl font-futuristic font-bold">
              <span className="text-white">What's </span>
              <span className="bg-gradient-to-r from-red-400 to-orange-500 bg-clip-text text-transparent">
                Hot
              </span>
            </h2>
            <Flame className="w-8 h-8 text-red-500 ml-3 animate-pulse" />
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Stay updated with the hottest trends and breaking news in gaming technology
          </p>
        </motion.div>

        {/* Trending Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Swiper
            className={`${styles.trendingSwiper} trending-swiper`}
            modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
              el: '.swiper-pagination',
            }}
            centeredSlides={true}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            effect="coverflow"
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            breakpoints={{
              640: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
          >
            {trendingTopics.map((topic) => (
              <SwiperSlide key={topic.id}>
                <motion.div
                  className="group glass rounded-xl overflow-hidden cursor-pointer h-96"
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Card Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={topic.image}
                      alt={topic.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 to-transparent" />
                    
                    {/* Trending Badge */}
                    {topic.trending && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-gradient-to-r from-red-500 to-orange-500 px-3 py-1 text-sm font-bold text-white rounded-full flex items-center space-x-1 animate-pulse">
                          <TrendingUp className="w-4 h-4" />
                          <span>TRENDING</span>
                        </span>
                      </div>
                    )}

                    {/* Category Badge */}
                    <div className="absolute top-4 right-4">
                      <span className="glass px-3 py-1 text-sm font-medium text-primary-400 rounded-full">
                        {topic.category}
                      </span>
                    </div>

                    {/* Meta Info Overlay */}
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white text-sm">
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{topic.views}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{topic.date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6 h-48 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-400 transition-colors line-clamp-2">
                        {topic.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed line-clamp-3">
                        {topic.description}
                      </p>
                    </div>
                    
                    {/* Read More Button */}
                    <motion.button
                      className="flex items-center space-x-2 text-primary-500 hover:text-primary-400 font-semibold group/btn mt-4"
                      whileHover={{ x: 5 }}
                    >
                      <span>Read More</span>
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </motion.button>
                  </div>

                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-xl" />
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        {/* View All Trending Button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.button
            className="glass px-8 py-4 rounded-lg font-semibold text-white border-2 border-red-500 hover:bg-red-500 transition-all duration-300 flex items-center space-x-2 mx-auto"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Flame className="w-5 h-5" />
            <span>View All Trending</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>

    </section>
  );
};

export default WhatsHot;
